import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
  SerializedError
} from '@reduxjs/toolkit';
import { GenerelConfig, HealthClaim, Product, Tenant } from '../../types';
import { SearchProductsRequest, Status } from '../types';
import appConfig from '../../config/app.config';
import { THUNK_NAMES, getProducts, getMoreProducts, getGeneralConfig } from './products.thunk';

interface statusObject {
  getProducts: Status;
  getTenantsToLocationsMappings: Status;
  getMoreProducts: Status;
  getGeneralConfig: Status;
}

interface ProductsState {
  list: Product[];
  status: statusObject;
  error: SerializedError | null;
  queryParams: SearchProductsRequest;
  searchbarString: string;
  generalConfig: GenerelConfig;
}

const urlSplit = window.location.href.split(`${appConfig.FE_URL}/products`);
const urlSearchString = urlSplit.length <= 1 ? '' : urlSplit[1];
const urlSearchParams = new URLSearchParams(urlSearchString);
const urlLimit = Number(urlSearchParams.get('limit')) ? Number(urlSearchParams.get('limit')) : 100;
const urlSkip = Number(urlSearchParams.get('skip'));
const initialLimit = urlLimit + urlSkip;
const queryParams: SearchProductsRequest = {
  searchString: urlSearchParams.get('searchString') ?? '',
  tenantId: urlSearchParams.get('tenantId') ?? '',
  locationId: urlSearchParams.get('locationId') ?? '',
  limit: initialLimit,
  skip: 0,
  searchFilter: 'all'
};

const initialState: ProductsState = {
  list: [],
  status: {
    getProducts: 'idle',
    getTenantsToLocationsMappings: 'idle',
    getMoreProducts: 'idle',
    getGeneralConfig: 'idle'
  },
  error: null,
  queryParams,
  searchbarString: queryParams.searchString ?? '',
  generalConfig: { tenantsAndLocations: {}, healthClaims: [] }
};

const slice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    updateQueryParams(state: ProductsState, action: PayloadAction<SearchProductsRequest>) {
      const newParams = { ...state.queryParams, ...action.payload };
      state.queryParams = newParams;
    },
    restartQueryParams(state: ProductsState) {
      state.queryParams = { limit: 100, skip: 0 };
      state.searchbarString = '';
    },
    changeSearchbarString(state: ProductsState, action: PayloadAction<string>) {
      state.searchbarString = action.payload;
    },
    restartSearchFilter(state: ProductsState) {
      state.queryParams.searchFilter = 'all';
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isFulfilled(getProducts, getMoreProducts, getGeneralConfig),
        (
          state: ProductsState,
          action: PayloadAction<
            Record<string, Tenant> | Product[] | Product | HealthClaim[] | GenerelConfig
          >
        ) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCTS:
              state.status.getProducts = 'idle';
              state.list = action.payload as Product[];
              break;
            case THUNK_NAMES.GET_MORE_PRODUCTS:
              state.status.getMoreProducts = 'idle';
              const newList = state.list.concat(action.payload as Product[]);
              state.list = newList;
              break;
            case THUNK_NAMES.GET_GENERAL_CONFIG:
              state.status.getGeneralConfig = 'idle';
              state.generalConfig = action.payload as GenerelConfig;
              break;
            default:
              break;
          }
          state.error = null;
        }
      )
      .addMatcher(
        isPending(getProducts, getMoreProducts, getGeneralConfig),
        (state: ProductsState, action) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCTS:
              state.status.getProducts = 'loading';
              break;
            case THUNK_NAMES.GET_MORE_PRODUCTS:
              state.status.getMoreProducts = 'loading';
              break;
            case THUNK_NAMES.GET_GENERAL_CONFIG:
              state.status.getGeneralConfig = 'loading';
              break;
            default:
              break;
          }
          state.error = null;
        }
      )
      .addMatcher(
        isRejected(getProducts, getMoreProducts, getGeneralConfig),
        (state: ProductsState, action) => {
          const requestType = action.type.split('/')[1];
          switch (requestType) {
            case THUNK_NAMES.GET_PRODUCTS:
              state.status.getProducts = 'error';
              break;
            case THUNK_NAMES.GET_MORE_PRODUCTS:
              state.status.getMoreProducts = 'error';
              break;
            case THUNK_NAMES.GET_GENERAL_CONFIG:
              state.status.getGeneralConfig = 'error';
              break;
            default:
              break;
          }
          state.error = action.error;
        }
      );
  }
});

export default slice;

export const selectors = {
  selectProducts: (state: ProductsState) => state.list,
  selectTenantsAndLocations: (state: ProductsState) => state.generalConfig.tenantsAndLocations,
  selectIsDataGridLoading: (state: ProductsState) =>
    state.status.getProducts === 'loading' ||
    state.status.getTenantsToLocationsMappings === 'loading',
  selectErrorOccuredInDataGrid: (state: ProductsState) =>
    state.status.getProducts === 'error' ||
    state.status.getTenantsToLocationsMappings === 'error' ||
    state.status.getMoreProducts === 'error',
  selectIsMoreProductsLoading: (state: ProductsState) => state.status.getMoreProducts === 'loading',
  selectQueryParams: (state: ProductsState) => state.queryParams,
  selectProductsLimit: (state: ProductsState) => {
    const limit = state.queryParams.limit ?? 100;
    const skip = state.queryParams.skip ?? 0;
    return skip + limit;
  },
  selectCurrentTenant: (state: ProductsState) => state.queryParams.tenantId ?? '',
  selectCurrentLocation: (state: ProductsState) => state.queryParams.locationId ?? '',
  selectSearchbarString: (state: ProductsState) => state.searchbarString,
  selectError: (state: ProductsState) => state.error,
  selectHealthClaims: (state: ProductsState) => state.generalConfig.healthClaims ?? '',
  selectCurrentSearchFilter: (state: ProductsState) => state.queryParams.searchFilter ?? 'all'
};
